import React from 'react';
import { Trophy, Brain, Heart, Timer, CheckSquare2, Briefcase, BookOpen } from 'lucide-react';
import { useCurrentView } from '../../hooks/useCurrentView';
import type { View } from '../../lib/viewRouter';
import { PageContainer } from '../layout/Page';

/**
 * The Action group: every "action type" is a sibling view, but they share one
 * persistent tab bar. ActionShell renders that bar ONCE (hoisted in App.tsx)
 * so switching tabs swaps only the content below — the bar itself never
 * unmounts. This is the single source of truth for the Action tabs; the old
 * per-view bars (ActionTabs, PageNavigation) are retired in favour of this.
 */

// Visible tabs. 'action' (the Action Center hub) is intentionally NOT a tab —
// it's the landing surface the bottom-nav "Action" item opens; from there the
// tabs lead into each action type.
export const ACTION_TABS = [
  { id: 'quests', label: 'Quests', icon: Trophy, color: '#eab308' },
  { id: 'abilities', label: 'Abilities', icon: Brain, color: '#8b5cf6' },
  { id: 'programs', label: 'Programs', icon: BookOpen, color: '#10b981' },
  { id: 'projects', label: 'Projects', icon: Briefcase, color: '#f59e0b' },
  { id: 'tasks', label: 'Tasks', icon: CheckSquare2, color: '#3b82f6' },
  { id: 'habits', label: 'Habits', icon: Heart, color: '#ec4899' },
  { id: 'routines', label: 'Routines', icon: Timer, color: '#a855f7' },
] as const satisfies ReadonlyArray<{ id: View; label: string; icon: typeof Trophy; color: string }>;

/**
 * Views that belong to the Action group — drive the bottom-nav/rail highlight
 * AND tell App.tsx which views to wrap in ActionShell. Includes 'action' (the
 * hub) even though it isn't a visible tab, so the hub keeps the shared bar and
 * keeps "Action" highlighted.
 */
export const ACTION_VIEWS = new Set<View>(['action', ...ACTION_TABS.map((t) => t.id)]);

interface ActionShellProps {
  currentView: View;
  children: React.ReactNode;
}

export function ActionShell({ currentView, children }: ActionShellProps) {
  const { setCurrentView } = useCurrentView();

  return (
    <PageContainer>
      {/* Persistent, horizontally-scrollable tab bar (won't crowd on mobile) */}
      <div className="bg-card rounded-lg border border-border p-1">
        <div
          className="flex gap-1 overflow-x-auto scrollbar-hide snap-x"
          role="tablist"
          aria-label="Action types"
        >
          {ACTION_TABS.map((tab) => {
            const active = currentView === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setCurrentView(tab.id)}
                className={`flex flex-1 min-w-max snap-start items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  active
                    ? 'bg-primary/10 text-primary nav-active-contrast accent-gradient-border'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                }`}
                style={active ? { backgroundColor: `${tab.color}15`, color: tab.color } : undefined}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="whitespace-nowrap">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Swappable content for the active action view */}
      {children}
    </PageContainer>
  );
}
